import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, MessageSquare, Users, Send, ThumbsUp, ChevronRight, Plus } from 'lucide-react';
import ExportBar from '../components/ExportBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMMUNITIES = [
  { id: 'all', bn: 'সব', en: 'All' },
  { id: 'mathematics', bn: 'গণিত', en: 'Mathematics' },
  { id: 'science', bn: 'বিজ্ঞান', en: 'Science' },
  { id: 'bangla', bn: 'বাংলা', en: 'Bangla' },
  { id: 'ict', bn: 'ICT', en: 'ICT' },
  { id: 'general', bn: 'সাধারণ', en: 'General' },
];

export default function Communication() {
  const { t, lang, currentTeacher, currentSchool } = useApp();
  const [tab, setTab] = useState('community');
  const [circulars, setCirculars] = useState([]);
  const [messages, setMessages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedComm, setSelectedComm] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [newCircular, setNewCircular] = useState({ title_bangla: '', content_bangla: '', priority: 'normal' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const commParam = selectedComm === 'all' ? 'all' : selectedComm;
    const fetchData = async () => {
      try { const r = await axios.get(`${API}/circulars`); setCirculars(r.data); } catch(e) {}
      try { const r = await axios.get(`${API}/messages`, { params: { teacher_id: currentTeacher?.teacher_id } }); setMessages(r.data); } catch(e) {}
      try { const r = await axios.get(`${API}/community/posts`, { params: { channel: commParam } }); setPosts(Array.isArray(r.data) ? r.data : []); } catch(e) {}
      setLoading(false);
    };
    fetchData();
  }, [currentTeacher, selectedComm]);

  const createPost = async () => {
    if (!newPost.content) return;
    await axios.post(`${API}/community/posts`, { teacher_id: currentTeacher.teacher_id, community: selectedComm === 'all' ? 'general' : selectedComm, title_bangla: newPost.title, body_bangla: newPost.content });
    toast.success(lang === 'bn' ? 'পোস্ট প্রকাশিত' : 'Post published');
    setNewPost({ title: '', content: '' }); setShowNew(false);
    const p = await axios.get(`${API}/community/posts`, { params: { channel: selectedComm === 'all' ? 'all' : selectedComm } });
    setPosts(p.data);
  };

  const createCircular = async () => {
    if (!newCircular.title_bangla || !newCircular.content_bangla) return;
    await axios.post(`${API}/circulars`, { school_id: currentSchool?.school_id, author_id: currentTeacher?.teacher_id, ...newCircular });
    toast.success(lang === 'bn' ? 'বিজ্ঞপ্তি প্রকাশিত' : 'Circular published');
    setNewCircular({ title_bangla: '', content_bangla: '', priority: 'normal' }); setShowNew(false);
    const c = await axios.get(`${API}/circulars`);
    setCirculars(c.data);
  };

  const upvote = async (postId) => {
    await axios.post(`${API}/community/posts/${postId}/upvote`);
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, upvotes: p.upvotes + 1 } : p));
  };

  const tabs = [
    { id: 'community', label: lang === 'bn' ? 'সম্প্রদায়' : 'Community', icon: Users },
    { id: 'circulars', label: lang === 'bn' ? 'বিজ্ঞপ্তি' : 'Circulars', icon: Bell },
    { id: 'best_practices', label: lang === 'bn' ? 'সেরা চর্চা' : 'Best Practices', icon: ThumbsUp },
    { id: 'ptm', label: lang === 'bn' ? 'PTM রেকর্ড' : 'PTM Record', icon: Users },
    { id: 'leaderboard', label: lang === 'bn' ? 'লিডারবোর্ড' : 'Leader Board', icon: Users },
    { id: 'messages', label: lang === 'bn' ? 'বার্তা' : 'Messages', icon: MessageSquare },
  ];

  if (loading) return <div className="text-center py-12 text-slate-400">{t('loading')}</div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="communication-page">
      <div className="card-flat p-4">
        <h2 className="text-base font-semibold font-bangla">{lang === 'bn' ? 'যোগাযোগ ও সম্প্রদায়' : 'Communication Hub'}</h2>
      </div>

      <div className="flex gap-1">
        {tabs.map(tb => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} data-testid={`comm-tab-${tb.id}`} onClick={() => { setTab(tb.id); setShowNew(false); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs transition-colors ${tab === tb.id ? 'bg-[#1B2A4A] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
              <Icon size={14} /><span className="font-bangla">{tb.label}</span>
            </button>
          );
        })}
      </div>

      {tab === 'circulars' && (
        <div className="space-y-4">
          {/* Circular Workflow Description */}
          <div className="card-surface" style={{borderLeft:'4px solid var(--color-accent)'}}>
            <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600}}>{lang === 'bn' ? 'বিজ্ঞপ্তি কর্মপ্রবাহ' : 'Circular Workflow'}</h4>
            <div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Circular Workflow' : 'বিজ্ঞপ্তি কর্মপ্রবাহ'}</div>
            <p style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)',lineHeight:1.6}}>
              {lang === 'bn' ? 'জেলা/মন্ত্রণালয় → প্রধান শিক্ষক গ্রহণ → সংশ্লিষ্ট শিক্ষকে বরাদ্দ → শিক্ষক স্বীকৃতি → সময়সীমার মধ্যে সম্মতি/ATR জমা' : 'District/MOE → Principal receives → Assigns to teacher → Teacher acknowledges → Submits compliance/ATR within deadline'}
            </p>
          </div>
          {/* Circular list with classification + compliance status */}
          {[
            {title:'Annual Exam Schedule 2026',titleBn:'বার্ষিক পরীক্ষার সূচি ২০২৬',from:'District Education Office',priority:'high',assignedTo:'All Teachers',status:'acknowledged',compliance:'Submitted',atr:'Exam hall arrangement completed, invigilator list prepared',deadline:'2026-04-01'},
            {title:'Teacher Training Workshop — April',titleBn:'শিক্ষক প্রশিক্ষণ কর্মশালা — এপ্রিল',from:'NAEM / MOE',priority:'urgent',assignedTo:'Science Teachers',status:'pending',compliance:'Pending',atr:null,deadline:'2026-04-10'},
            {title:'Mid-Day Meal Quality Audit',titleBn:'মধ্যাহ্ন ভোজন মান নিরীক্ষা',from:'Directorate of Primary Education',priority:'normal',assignedTo:'Meal Coordinator',status:'acknowledged',compliance:'In Progress',atr:'Audit checklist being prepared',deadline:'2026-04-15'},
            {title:'Dengue Prevention Measures',titleBn:'ডেঙ্গু প্রতিরোধ ব্যবস্থা',from:'IEDCR / District Health',priority:'urgent',assignedTo:'All Teachers + Parents',status:'read',compliance:'Submitted',atr:'Awareness session conducted, school premises cleaned',deadline:'2026-03-30'},
            {title:'National Education Day Celebration',titleBn:'জাতীয় শিক্ষা দিবস উদযাপন',from:'Ministry of Education',priority:'normal',assignedTo:'Cultural Committee',status:'pending',compliance:'Not Started',atr:null,deadline:'2026-04-20'},
          ].map((circ,i) => (
            <div key={i} className="card-surface" style={{padding:'var(--space-4) var(--space-5)',borderLeft: circ.priority === 'urgent' ? '3px solid var(--color-error)' : circ.priority === 'high' ? '3px solid var(--color-warning)' : '3px solid var(--color-separator)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',gap:'var(--space-2)',flexWrap:'wrap',marginBottom:'var(--space-2)'}}>
                    <span className={"badge " + (circ.priority==='urgent'?'badge-error':circ.priority==='high'?'badge-warning':'badge-neutral')}>{circ.priority.toUpperCase()}</span>
                    <span className={"badge " + (circ.status==='acknowledged'?'badge-success':circ.status==='read'?'badge-accent':'badge-neutral')}>{circ.status === 'acknowledged' ? 'ACKNOWLEDGED' : circ.status === 'read' ? 'READ' : 'PENDING'}</span>
                    <span className="badge badge-neutral">{circ.from}</span>
                  </div>
                  <div style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? circ.titleBn : circ.title}</div>
                  <div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? circ.title : circ.titleBn}</div>
                  <div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)'}}>
                    {lang === 'bn' ? 'বরাদ্দ' : 'Assigned to'}: {circ.assignedTo} | {lang === 'bn' ? 'সময়সীমা' : 'Deadline'}: {circ.deadline}
                  </div>
                  {circ.atr && (
                    <div style={{fontSize:'var(--text-footnote)',marginTop:'var(--space-2)',padding:'var(--space-2) var(--space-3)',background:'var(--color-success-bg)',borderRadius:'var(--radius-sm)',color:'var(--color-success)'}}>
                      <strong>ATR:</strong> {circ.atr}
                    </div>
                  )}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'var(--space-2)',alignItems:'flex-end',flexShrink:0,marginLeft:'var(--space-4)'}}>
                  <span className={"badge " + (circ.compliance==='Submitted'?'badge-success':circ.compliance==='In Progress'?'badge-accent':circ.compliance==='Pending'?'badge-warning':'badge-neutral')}>{circ.compliance}</span>
                  {circ.compliance !== 'Submitted' && <button className="btn btn-sm" style={{background:'var(--color-accent)',color:'white',padding:'4px 10px',fontSize:11}}>{lang === 'bn' ? 'ATR জমা দিন' : 'Submit ATR'}</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'circulars_old_REMOVED' && (
        <div className="space-y-3">
          <button data-testid="new-circular-btn" onClick={() => setShowNew(!showNew)} className="flex items-center gap-1.5 text-xs px-3 py-2 bg-[#1D9E75] text-white rounded">
            <Plus size={14} />{lang === 'bn' ? 'নতুন বিজ্ঞপ্তি' : 'New Circular'}
          </button>
          {showNew && (
            <div className="card-flat p-4 space-y-3">
              <input data-testid="circular-title" value={newCircular.title_bangla} onChange={e => setNewCircular({ ...newCircular, title_bangla: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-3 py-2 font-bangla" placeholder={lang === 'bn' ? 'শিরোনাম' : 'Title'} />
              <textarea data-testid="circular-content" value={newCircular.content_bangla} onChange={e => setNewCircular({ ...newCircular, content_bangla: e.target.value })}
                className="w-full text-sm border border-slate-200 rounded px-3 py-2 font-bangla" rows={3} placeholder={lang === 'bn' ? 'বিষয়বস্তু' : 'Content'} />
              <div className="flex gap-2">
                {['normal', 'high', 'urgent'].map(p => (
                  <button key={p} onClick={() => setNewCircular({ ...newCircular, priority: p })}
                    className={`flex-1 py-1.5 text-xs rounded-sm border ${newCircular.priority === p ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'border-slate-200'}`}>
                    {p === 'normal' ? (lang === 'bn' ? 'সাধারণ' : 'Normal') : p === 'high' ? (lang === 'bn' ? 'গুরুত্বপূর্ণ' : 'High') : (lang === 'bn' ? 'জরুরি' : 'Urgent')}
                  </button>
                ))}
              </div>
              <button data-testid="submit-circular" onClick={createCircular} className="w-full py-2 bg-[#1D9E75] text-white rounded-sm text-sm font-bangla">{t('submit')}</button>
            </div>
          )}
          {circulars.map(c => (
            <div key={c.circular_id} className={`card-flat p-4 ${c.priority === 'urgent' ? 'border-l-4 border-l-red-500' : c.priority === 'high' ? 'border-l-4 border-l-amber-500' : ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <Bell size={14} className={c.priority === 'urgent' ? 'text-red-500' : 'text-[#1B2A4A]'} />
                <h4 className="text-sm font-semibold font-bangla">{c.title_bangla}</h4>
              </div>
              <p className="text-xs text-slate-600 font-bangla">{c.content_bangla}</p>
              <p className="text-[10px] text-slate-300 mt-2">{new Date(c.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'messages' && (
        <div className="space-y-3">
          {messages.length === 0 && <p className="text-center text-sm text-slate-400 py-8">{lang === 'bn' ? 'কোনো বার্তা নেই' : 'No messages'}</p>}
          {messages.map(m => (
            <div key={m.message_id} className="card-flat p-4">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare size={14} className="text-[#1B2A4A]" />
                <h4 className="text-sm font-medium">{m.subject}</h4>
                {m.is_broadcast && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{lang === 'bn' ? 'সম্প্রচার' : 'Broadcast'}</span>}
              </div>
              <p className="text-xs text-slate-600">{m.content}</p>
              <p className="text-[10px] text-slate-300 mt-1">{new Date(m.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'best_practices' && (
        <div className="space-y-4">
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'সেরা চর্চা ও চ্যালেঞ্জ' : 'Best Practices & Challenges'}</h3>
            <div style={{fontSize:'11px',color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Best Practices & Challenges' : 'সেরা চর্চা ও চ্যালেঞ্জ'}</div>
          </div>
          {[{title:'Peer Learning Groups — Grade 3 Mathematics',titleBn:'পিয়ার লার্নিং গ্রুপ — শ্রেণি ৩ গণিত',author:'Fatema Begum',type:'practice',desc:'Pairing high-performing students with struggling ones improved test scores by 23% in one term.'},{title:'Managing 50+ Students with Limited Space',titleBn:'সীমিত জায়গায় ৫০+ শিক্ষার্থী ব্যবস্থাপনা',author:'Kamrul Hasan',type:'challenge',desc:'Our classroom fits 30 but has 55 students. U-shape seating and group leaders help manage.'},{title:'Using WhatsApp for Parent Engagement',titleBn:'অভিভাবক সম্পৃক্ততায় হোয়াটসঅ্যাপ ব্যবহার',author:'Nasrin Akter',type:'practice',desc:'Created class WhatsApp groups. 85% parents now respond within 24 hours vs 30% before.'},{title:'Digital Attendance Reduced Proxy Marking',titleBn:'ডিজিটাল হাজিরায় প্রক্সি মার্কিং কমেছে',author:'Rafiqul Islam',type:'practice',desc:'GPS-verified attendance eliminated proxy marking completely. Attendance accuracy now 99.8%.'}].map((bp,i) => (
            <div key={i} className="card-surface" style={{padding:'var(--space-5)',borderLeft: bp.type==='challenge' ? '3px solid var(--color-warning)' : '3px solid var(--color-accent)'}}>
              <div className="flex items-center gap-2" style={{marginBottom:'var(--space-2)'}}>
                <span className={"badge " + (bp.type==='practice' ? 'badge-success' : 'badge-warning')}>{bp.type==='practice' ? (lang==='bn'?'সেরা চর্চা':'BEST PRACTICE') : (lang==='bn'?'চ্যালেঞ্জ':'CHALLENGE')}</span>
              </div>
              <h4 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? bp.titleBn : bp.title}</h4>
              <div style={{fontSize:'10px',color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? bp.title : bp.titleBn}</div>
              <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)',lineHeight:1.6}}>{bp.desc}</p>
              <p style={{fontSize:'var(--text-caption)',color:'var(--color-label-tertiary)',marginTop:'var(--space-2)'}}>{bp.author}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'ptm' && (
        <div className="space-y-4">
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'অভিভাবক-শিক্ষক সভা রেকর্ড' : 'Parent-Teacher Meeting Record'}</h3>
            <div style={{fontSize:'11px',color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'PTM Record' : 'অভিভাবক-শিক্ষক সভা'}</div>
            <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)'}}>{lang === 'bn' ? 'অভিভাবকদের সাথে শিক্ষার্থীদের প্রতিবেদন শেয়ার করুন' : 'Share student reports with parents/guardians via WhatsApp or Email'}</p>
          </div>
          {[{student:'Rakib Hasan',date:'2026-03-15',status:'Completed',method:'WhatsApp'},{student:'Nusrat Jahan',date:'2026-03-15',status:'Completed',method:'Email'},{student:'Tanvir Hossain',date:'2026-03-15',status:'Pending',method:'--'},{student:'Fariha Tasnim',date:'2026-03-20',status:'Scheduled',method:'WhatsApp'}].map((ptm,i) => (
            <div key={i} className="card-surface" style={{padding:'var(--space-4)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div><div style={{fontSize:'var(--text-subhead)',fontWeight:500}}>{ptm.student}</div><div style={{fontSize:'var(--text-caption)',color:'var(--color-label-tertiary)'}}>{ptm.date}</div></div>
              <div style={{display:'flex',alignItems:'center',gap:'var(--space-3)'}}>
                <span className={"badge " + (ptm.status==='Completed'?'badge-success':ptm.status==='Pending'?'badge-warning':'badge-accent')}>{ptm.status}</span>
                <span style={{fontSize:'var(--text-caption)',color:'var(--color-label-tertiary)'}}>{ptm.method}</span>
                <button className="btn btn-sm" style={{background:'var(--color-accent)',color:'white',padding:'4px 10px',fontSize:11}}>{lang === 'bn' ? 'শেয়ার' : 'Share'}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'leaderboard' && (
        <div className="space-y-4">
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'লিডারবোর্ড' : 'Leader Board'}</h3>
            <div style={{fontSize:'11px',color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Leader Board' : 'লিডারবোর্ড'}</div>
          </div>
          {['Students','Teachers','Principals'].map(cat => (
            <div key={cat} className="card-surface" style={{padding:'var(--space-5)'}}>
              <h4 style={{fontSize:'var(--text-subhead)',fontWeight:600,marginBottom:'var(--space-3)'}}>{lang === 'bn' ? ({Students:'শিক্ষার্থী',Teachers:'শিক্ষক',Principals:'প্রধান শিক্ষক'}[cat]) : cat}</h4>
              {[{name:cat==='Students'?'Rakib Hasan':cat==='Teachers'?'Fatema Begum':'Kamrul Hasan',pts:2450},{name:cat==='Students'?'Nusrat Jahan':cat==='Teachers'?'Nasrin Akter':'Shahjahan Ahmed',pts:2180},{name:cat==='Students'?'Tanvir Hossain':cat==='Teachers'?'Shamsul Alam':'Rafiqul Islam',pts:1950}].map((p,i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'var(--space-3)',padding:'var(--space-2) 0',borderBottom:i<2?'1px solid var(--color-separator)':'none'}}>
                  <span style={{width:24,height:24,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,background:i===0?'#C9A84C':i===1?'#aaa':'#cd7f32',color:'white'}}>{i+1}</span>
                  <span style={{flex:1,fontSize:'var(--text-subhead)'}}>{p.name}</span>
                  <span style={{fontSize:'var(--text-subhead)',fontWeight:600,color:'var(--color-accent)'}}>{p.pts} pts</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === 'community' && (
        <div className="space-y-4">
          {/* Channel filter chips */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {COMMUNITIES.map(c => (
              <button key={c.id} onClick={() => setSelectedComm(c.id)}
                className="btn btn-sm" style={{
                  background: selectedComm === c.id ? 'var(--color-accent)' : 'var(--color-surface-2)',
                  color: selectedComm === c.id ? 'white' : 'var(--color-label-secondary)',
                  whiteSpace: 'nowrap', fontSize: 'var(--text-footnote)'
                }}>
                {lang === 'bn' ? c.bn : c.en}
              </button>
            ))}
          </div>

          {/* New post button */}
          <button data-testid="new-post-btn" onClick={() => setShowNew(!showNew)}
            className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>
            <Plus size={14} />{lang === 'bn' ? 'নতুন পোস্ট' : 'New Post'}
          </button>

          {/* New post form */}
          {showNew && (
            <div className="card-surface space-y-3">
              <input data-testid="post-title" value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })}
                className="input-filled" style={{ fontSize: 'var(--text-subhead)' }} placeholder={lang === 'bn' ? 'শিরোনাম' : 'Title'} />
              <textarea data-testid="post-content" value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })}
                className="input-filled" rows={3} style={{ fontSize: 'var(--text-subhead)' }} placeholder={lang === 'bn' ? 'আপনার মতামত লিখুন...' : 'Share your thoughts...'} />
              <button data-testid="submit-post" onClick={createPost}
                className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white' }}>
                <Send size={14} />{t('submit')}
              </button>
            </div>
          )}

          {/* Posts list */}
          {posts.length === 0 && (
            <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
              <Users size={40} style={{ color: 'var(--color-label-tertiary)', margin: '0 auto 12px' }} />
              <p style={{ color: 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'কোনো পোস্ট পাওয়া যায়নি' : 'No posts in this channel yet'}</p>
            </div>
          )}

          {posts.map(p => (
            <div key={p.post_id} className="card-surface" data-testid={`post-${p.post_id}`}
              style={{ borderLeft: p.is_pinned ? '3px solid var(--color-red)' : 'none' }}>
              <div className="flex items-start justify-between gap-3">
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Post type badge + pinned indicator */}
                  <div className="flex items-center gap-2 mb-2">
                    {p.is_pinned && (
                      <span className="badge badge-error" style={{ fontSize: 9 }}>{lang === 'bn' ? 'পিন' : 'PINNED'}</span>
                    )}
                    {p.post_type && (
                      <span className="badge badge-neutral" style={{ fontSize: 9 }}>
                        {p.post_type === 'question' ? (lang === 'bn' ? 'প্রশ্ন' : 'QUESTION') :
                         p.post_type === 'tip' ? (lang === 'bn' ? 'পরামর্শ' : 'TIP') :
                         p.post_type === 'resource' ? (lang === 'bn' ? 'রিসোর্স' : 'RESOURCE') :
                         p.post_type === 'announcement' ? (lang === 'bn' ? 'ঘোষণা' : 'ANNOUNCEMENT') :
                         (lang === 'bn' ? 'আলোচনা' : 'DISCUSSION')}
                      </span>
                    )}
                    {p.community && (
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-accent)', fontWeight: 500 }}>
                        {COMMUNITIES.find(c => c.id === p.community)?.[lang === 'bn' ? 'bn' : 'en'] || p.community}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  {(p.title || p.title_bangla) && (
                    <h4 style={{ fontSize: 'var(--text-body)', fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 'var(--space-2)' }}>
                      {lang === 'bn' ? (p.title_bangla || p.title) : (p.title || p.title_bangla)}
                    </h4>
                  )}

                  {/* Content */}
                  <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', lineHeight: 1.6 }}>
                    {lang === 'bn' ? (p.content_bangla || p.content) : (p.content || p.content_bangla)}
                  </p>

                  {/* Author + time */}
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-3)' }}>
                    {lang === 'bn' ? (p.teacher_name || '') : (p.teacher_name_english || p.teacher_name || '')} | {new Date(p.created_at).toLocaleDateString()}
                  </p>

                  {/* Tags */}
                  {p.tags?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {p.tags.map(tag => (
                        <span key={tag} style={{ fontSize: 'var(--text-caption)', padding: '1px 6px', borderRadius: 'var(--radius-pill)',
                          background: 'var(--color-surface-2)', color: 'var(--color-label-tertiary)' }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upvote */}
                <button onClick={() => upvote(p.post_id)} className="flex flex-col items-center gap-0.5" style={{ padding: 'var(--space-2)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-2)', border: 'none', cursor: 'pointer', minWidth: 44 }}>
                  <ThumbsUp size={16} style={{ color: 'var(--color-accent)' }} />
                  <span style={{ fontSize: 'var(--text-footnote)', fontWeight: 600, color: 'var(--color-accent)' }}>{p.upvotes || 0}</span>
                </button>
              </div>

              {/* Replies */}
              {p.replies?.length > 0 && (
                <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-separator)' }}>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginBottom: 'var(--space-2)', fontWeight: 500 }}>
                    {p.replies.length} {lang === 'bn' ? 'টি উত্তর' : (p.replies.length === 1 ? 'reply' : 'replies')}
                  </p>
                  {p.replies.slice(0, 3).map(r => (
                    <div key={r.reply_id} style={{ padding: 'var(--space-2) 0 var(--space-2) var(--space-4)', borderLeft: '2px solid var(--color-separator)', marginBottom: 'var(--space-2)' }}>
                      <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', lineHeight: 1.5 }}>
                        {lang === 'bn' ? (r.content_bangla || r.content) : (r.content || r.content_bangla)}
                      </p>
                      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-quarternary)', marginTop: 2 }}>
                        {lang === 'bn' ? (r.teacher_name || '') : (r.teacher_name_english || r.teacher_name || '')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
